import { useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Select,
  ThemeIcon,
  Loader,
  Paper,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconDownload, IconFileSpreadsheet } from "@tabler/icons-react";
import axios from "axios";
import { Endpoints } from "../../lib/endpoints";

type TimeRange = "24h" | "7d" | "30d" | "custom" | "all";

interface ExportModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  type:
    | "users"
    | "activities"
    | "campaigns"
    | "proofs"
    | "withdrawals"
    | "audit";
  additionalFilters?: React.ReactNode;
  onExport: (filters: any) => Promise<void>;
}

export const ExportModal = ({
  opened,
  onClose,
  title,
  type,
  additionalFilters,
  onExport,
}: ExportModalProps) => {
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<TimeRange>("all");
  const [customDates, setCustomDates] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const handleExport = async () => {
    setLoading(true);
    try {
      // Calculate dates
      let startDate: Date | undefined;
      let endDate: Date | undefined;
      const now = new Date();

      switch (range) {
        case "24h":
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case "7d":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "30d":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case "custom":
          if (customDates[0]) startDate = customDates[0];
          if (customDates[1]) endDate = customDates[1];
          break;
      }

      await onExport({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        // Any other filters should be handled by the parent passed in 'onExport' wrapper
        // if they depend on local state there, OR passed via additional props if we want this component to manage them.
        // For simplicity, parent handles specific filters via closure in onExport,
        // while this modal handles the mandated Date selection.
      });
      onClose();
    } catch (e) {
      console.error(e);
      // Parent should handle alert/toast
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      radius="lg"
      padding="xl"
      title={
        <Group>
          <ThemeIcon size="lg" radius="md" variant="light" color="green">
            <IconFileSpreadsheet size={20} />
          </ThemeIcon>
          <Text fw={700} size="lg">
            {title}
          </Text>
        </Group>
      }
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
    >
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          Select the data range and filters for your CSV export. The file will
          download automatically once generated.
        </Text>

        <Paper p="md" withBorder radius="md" bg="var(--bg-surface-2)">
          <Stack gap="sm">
            <Text size="sm" fw={600}>
              Time Period
            </Text>
            <Select
              data={[
                { value: "all", label: "All Time" },
                { value: "24h", label: "Last 24 Hours" },
                { value: "7d", label: "Last 7 Days" },
                { value: "30d", label: "Last 30 Days" },
                { value: "custom", label: "Custom Range" },
              ]}
              value={range}
              onChange={(v) => setRange(v as TimeRange)}
              allowDeselect={false}
            />
            {range === "custom" && (
              <DatePickerInput
                type="range"
                placeholder="Pick dates"
                value={customDates}
                onChange={(value) =>
                  setCustomDates(value as [Date | null, Date | null])
                }
                clearable
              />
            )}
          </Stack>
        </Paper>

        {additionalFilters && (
          <Paper p="md" withBorder radius="md" bg="var(--bg-surface-2)">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Additional Filters
              </Text>
              {additionalFilters}
            </Stack>
          </Paper>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            leftSection={!loading && <IconDownload size={18} />}
            color="green"
            onClick={handleExport}
            loading={loading}
          >
            Export CSV
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
